import React from "react";
import { formatUtcDate } from "../../helpers/utils";
import { useDispatch } from "react-redux";
import { showNotification } from "../../store/toasterDuck";
import type { CatalogueObject } from "../../types/catalog";
import { getDisplayObjectValue, norad2alpha5 } from "../../utils/catalogObject";
import { CatalogDetailsCard } from "./CatalogDetailsCard";

export const CatalogObjectDetails = ({
  object,
}: {
  object: CatalogueObject;
}) => {
  const dispatch = useDispatch();
  const rawNorad = getDisplayObjectValue(object?.noradId);
  const { formattedNorad, noradError } = React.useMemo(() => {
    try {
      return { formattedNorad: norad2alpha5(rawNorad), noradError: "" };
    } catch (error) {
      return {
        formattedNorad: "",
        noradError:
          error instanceof Error
            ? error.message
            : "Invalid NORAD identifier format",
      };
    }
  }, [rawNorad]);

  React.useEffect(() => {
    if (!noradError) {
      return;
    }

    dispatch(
      showNotification({
        message: `Unable to display NORAD identifier: ${noradError}`,
        intent: "danger",
      }),
    );
  }, [dispatch, noradError]);

  if (!object) return null;

  const identityFields = [
    { label: "Satellite name", value: getDisplayObjectValue(object.name) },
    {
      label: "NORAD",
      value: formattedNorad,
    },
    { label: "COSPAR", value: getDisplayObjectValue(object.cosparId) },
    { label: "Organization", value: getDisplayObjectValue(object.operator) },
    { label: "Source", value: getDisplayObjectValue(object.source) },
  ];

  const objectFields = [
    {
      label: "Type of object",
      value: getDisplayObjectValue(object.typeOfObject),
    },
    { label: "Status", value: getDisplayObjectValue(object.status) },
    { label: "Country", value: getDisplayObjectValue(object.country) },
    { label: "Launch site", value: getDisplayObjectValue(object.launchSite) },
    {
      label: "Launch date (UTC)",
      value: formatUtcDate(object.launchDate?.value),
    },
    {
      label: "Decay date (UTC)",
      value: formatUtcDate(object.decayDate?.value),
    },
    {
      label: "Latest update (UTC)",
      value: formatUtcDate(object.latestUpdate?.value),
    },
    {
      label: "Creation date (UTC)",
      value: formatUtcDate(object.createdAt?.value),
    },
  ];

  const physicalFields = [
    {
      label: "RCS",
      value: getDisplayObjectValue(object.rcs, "m^2"),
    },
    {
      label: "Mass",
      value: getDisplayObjectValue(object.mass, "kg"),
    },
    {
      label: "Hard body radius",
      value: getDisplayObjectValue(object.hbr, "m"),
    },
    {
      label: "Length",
      value: getDisplayObjectValue(object.length, "m"),
    },
    {
      label: "Width",
      value: getDisplayObjectValue(object.width, "m"),
    },
    {
      label: "Span",
      value: getDisplayObjectValue(object.span, "m"),
    },
    {
      label: "Average area",
      value: getDisplayObjectValue(object.avgArea, "m^2"),
    },
    { label: "Shape", value: getDisplayObjectValue(object.shape) },
  ];

  const orbitalFields = [
    {
      label: "Orbital period",
      value: getDisplayObjectValue(object.period, "min"),
    },
    {
      label: "Perigee height",
      value: getDisplayObjectValue(object.perigee, "km"),
    },
    {
      label: "Apogee height",
      value: getDisplayObjectValue(object.apogee, "km"),
    },
    { label: "Orbital regime", value: getDisplayObjectValue(object.regime) },
    {
      label: "Inclination",
      value: getDisplayObjectValue(object.inclination, "deg"),
    },
    {
      label: "RAAN",
      value: getDisplayObjectValue(object.raan, "deg"),
    },
    {
      label: "Argument of perigee",
      value: getDisplayObjectValue(object.aop, "deg"),
    },
  ];

  return (
    <div className="h-full w-full overflow-y-auto bg-slate-50/40 p-5">
      <div className="flex w-full flex-col gap-5">
        <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <CatalogDetailsCard
            title="Identity"
            description="Core identifiers and provenance."
            fields={identityFields}
          />
          <CatalogDetailsCard
            title="Object"
            description="Classification, launch and operational metadata."
            fields={objectFields}
          />
        </section>

        <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <CatalogDetailsCard
            title="Physical properties"
            description="Shape and dimensional characteristics."
            fields={physicalFields}
          />
          <CatalogDetailsCard
            title="Orbit"
            description="Keplerian parameters and orbital regime."
            fields={orbitalFields}
          />
        </section>
      </div>
    </div>
  );
};
